import { Injectable, Injector } from '@angular/core';
import { SdkBase } from '../utilities/sdk-base/sdk.base';
import { map, Observable, of, retry } from 'rxjs';
import { PublicFileResourceToFileMapper } from '../models/mappers/public-file-resource-to-file.mapper';
import { FileRecord } from '../models/ui/file.model';
import { PublicFileResource } from '../models/resource/public-file.resource';
import { FileDownloadDetailsResource } from '../models/resource/file-download-details.resource';
import { CompletedUpload } from '../models/ui/completed-upload.model';
import { FileUploadResponsePackageResource } from '../models/resource/file-upload-response-package.resource';
import { FileUploadRequestResource } from '../models/resource/file-upload-request.resource';
import { FileUploadRequestPackageResource } from '../models/resource/file-upload-request-package.resource';
import { FilePartUploadInstructionsResource } from '../models/resource/file-part-upload-instructions.resource';
import { FileUploadInstructionResource } from '../models/resource/file-upload-instruction.resource';
import { CompletedUploadPart } from '../models/ui/completed-upload-part.model';
import { FileDownloadRequestResource } from '../models/resource/file-download-request.resource';

const publicFileResourceToFileMapper = new PublicFileResourceToFileMapper();

@Injectable({
	providedIn: 'root'
})
export class FileSdk extends SdkBase {
	constructor(injector: Injector) {
		super(injector);
	}

	public getFileDetails(params: GetFileDetailsParams): Observable<FileRecord> {
		if (params?.sharedId) {
			return this.makeHttpRequest<PublicFileResource>('get', `api2/file/${ params.sharedId }/details`).pipe(
				map((response) => {
					return publicFileResourceToFileMapper.map(response);
				})
			);
		}
		return of(null);
	}

	public download(params: GetFileDownloadUrlParams): Observable<string> {
		if (params?.sharedId) {
			return this.makeHttpRequest<FileDownloadDetailsResource>('get', `api2/file/${ params?.sharedId }/url`)
				.pipe(
					map((response) => response.url)
				);
		}
		return of(null);
	}

	public getFileDownloadUrl(params: FileDownloadRequestResource): Observable<{url: string, fileSize: number}> {
		return this.makeHttpRequest<{url: string, fileSize: number}>('post', `api2/file/url`, params);
	}

	public requestUploadInstructions(uploadPackage: FileUploadRequestPackageResource): Observable<FileUploadResponsePackageResource> {
		const items = uploadPackage.requests.map((item) => {
			return {
				fileName: item.fileName,
				fileSize: item.fileSize,
				contentType: item.contentType,
				filePath: item.filePath
			} as FileUploadRequestResource;
		});
		return this.makeHttpRequest<FileUploadResponsePackageResource>('post', `api2/file/upload/create`, {
			requests: items,
			uploadBatchId: uploadPackage.uploadBatchId
		});
	}

	public requestProductionUploadInstructions(uploadPackage: FileUploadRequestPackageResource): Observable<FileUploadResponsePackageResource> {
		const items = uploadPackage.requests.map((item) => {
			return {
				fileName: item.fileName,
				fileSize: item.fileSize,
				contentType: item.contentType,
				filePath: item.filePath
			} as FileUploadRequestResource;
		});
		return this.makeHttpRequest<FileUploadResponsePackageResource>('post', `api2/file/production-upload/create`, {
			requests: items,
			uploadBatchId: uploadPackage.uploadBatchId
		});
	}

	public uploadFilePartToS3(params: UploadFilePartToS3Params): Observable<CompletedUploadPart> {
		return this.httpClient.put(params?.partInstruction.uploadUrl, params?.filePart, {
			headers: {
				'content-type': params?.contentType
			},
			observe: 'response'
		}).pipe(
			retry(3),
			map((httpResponse) => {
				const completedPart = new CompletedUploadPart();
				completedPart.partNumber = params?.partInstruction.partNumber;
				completedPart.eTag = httpResponse.headers.get('ETag').replaceAll('"', '');
				completedPart.isLast = params?.isLast;
				completedPart.fileName = params?.partInstruction.fileName;
				completedPart.partSize = params?.filePart.size;
				return completedPart;
			})
		);
	}

	public uploadSingleFileToS3(params: UploadSingleFileToS3Params): Observable<CompletedUpload> {
		return this.httpClient.put(params?.instruction.uploadUrl, params?.file, {
				headers: {
					'content-type': params?.contentType,
				},
				observe: 'response'
			}
		).pipe(
			retry(3),
			map(() => {
				const completedUpload = new CompletedUpload();
				completedUpload.documentId = params.instruction.documentId;
				completedUpload.originalFileId = params.instruction.fileId;
				completedUpload.fileSize = params?.file.size;
				completedUpload.fileName = params?.instruction.fileName;
				return completedUpload;
			})
		);
	}

	public completeUploads(uploads: CompletedUpload[]): Observable<void> {
		return this.makeHttpRequest<void>('post', `api2/file/upload/complete`, {
			completedUploads: uploads
		}).pipe(
			retry(3)
		);
	}

	public completeProductionUploads(uploads: CompletedUpload[]): Observable<void> {
		return this.makeHttpRequest<void>('post', `api2/file/production-upload/complete`, {
			completedUploads: uploads
		}).pipe(
			retry(3)
		);
	}
}

interface UploadSingleFileToS3Params {
	instruction: FileUploadInstructionResource;
	file: Blob;
	contentType: string;
}

interface UploadFilePartToS3Params {
	partInstruction: FilePartUploadInstructionsResource;
	filePart: Blob;
	contentType: string;
	isLast: boolean;
}

interface GetFileDetailsParams {
	sharedId: string;
}

interface GetFileDownloadUrlParams {
	sharedId: string;
}

